import { useMemo } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { formatRenderIssues, inspectKernelRender } from "../../lib/kernelRenderValidation.js";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { KernelEmpty, useLinkLabels, linkLabel, dimensionLabel } from "./shared.jsx";

export const kind = "bars";

export function Component({ spec, data, onOpenScope, onFocus }) {
    const validation = useMemo(
        () => inspectKernelRender(spec, data),
        [data, spec],
    );
    const rows = useMemo(() => {
        const arr = Array.isArray(data) ? data : [];
        const max = Math.max(0, ...arr.map((r) => asNumber(r?.[spec.valueField])));
        const limited = arr.slice(0, Math.max(1, spec.maxItems || 10));
        return { rows: limited, max };
    }, [data, spec.valueField, spec.maxItems]);

    const linkSpec = spec.link;
    useLinkLabels(rows.rows, linkSpec);

    if (validation.status === "invalid_render") {
        return <KernelEmpty message={formatRenderIssues(validation.issues)} />;
    }
    if (!rows.rows.length) return <KernelEmpty message={spec.empty || "no rows"} />;

    const color = sanitizeRenderColor(spec.color, "var(--green)");
    const labeller = (row) => linkLabel(
        linkSpec,
        row,
        dimensionLabel(row?.[spec.labelField], spec.emptyLabel),
        spec.unresolvedText,
    );
    const valueFmt = (v) => formatValue(v, spec.format || "number");

    return (
        <div className="space-y-[3px]">
            {rows.rows.map((row, i) => {
                const v = asNumber(row?.[spec.valueField]);
                const pct = rows.max > 0 ? Math.max(0, Math.min(100, (v / rows.max) * 100)) : 0;
                const id = linkSpec?.idField ? row?.[linkSpec.idField] : null;
                const displayLabel = labeller(row);
                const labelHandler = id && onOpenScope ? () => onOpenScope({
                    dim: linkSpec.dim,
                    ids: [String(id)],
                    label: linkSpec.labelField ? row?.[linkSpec.labelField] : displayLabel,
                }) : null;
                const focusField = spec.labelField;
                const focusValue = focusField ? row?.[focusField] : null;
                const focusHandler = focusValue != null && onFocus
                    ? () => onFocus(focusField, focusValue)
                    : null;
                const focusActive = spec.focusField === focusField;
                const focused = focusActive && Object.is(spec.focusValue, focusValue);
                const BarTrack = focusHandler ? "button" : "div";
                return (
                    <div
                        key={i}
                        data-focus-state={focusActive ? (focused ? "selected" : "dimmed") : undefined}
                        className={`grid grid-cols-[minmax(80px,30%)_minmax(0,1fr)_auto] items-center gap-[8px] text-[11px] leading-[1.3]${focusActive && !focused ? " opacity-40" : ""}`}
                    >
                        {/* Button only when clickable — a DISABLED button
                            swallows mouseenter, which would kill the hover
                            tooltip on non-linked labels. Non-clickable → div. */}
                        <OverflowTooltip
                            as={labelHandler ? "button" : "div"}
                            {...(labelHandler ? { type: "button", onClick: labelHandler } : {})}
                            text={displayLabel}
                            className={`min-w-0 font-mono text-[color:var(--ink)] text-left${labelHandler ? " cursor-pointer hover:underline" : ""}`}
                        >
                            {displayLabel}
                        </OverflowTooltip>
                        <BarTrack
                            {...(focusHandler ? {
                                type: "button",
                                onClick: focusHandler,
                                "aria-label": `Focus ${labeller(row)}`,
                                "aria-pressed": focused,
                            } : {})}
                            className={`relative h-[6px] w-full rounded-[1px] bg-[color:var(--line-soft)]${focusHandler ? " cursor-pointer outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ink)]" : ""}`}
                        >
                            <div
                                className="absolute inset-y-0 left-0 rounded-[1px]"
                                style={{
                                    width: `${pct}%`,
                                    background: sanitizeRenderColor(color),
                                }}
                            />
                        </BarTrack>
                        <span className="font-mono text-[10.5px] text-[color:var(--muted)] tabular-nums">
                            {valueFmt(v)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
